<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaMenuSurface from '../../base/GaiaMenuSurface.vue';
import GaiaQuote from '../../base/GaiaQuote.vue';
import GaiaText from '../../base/GaiaText.vue';
import { gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** The extraction's reason — why the agent answered the way it did. */
  reason: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** The source passages it read; the first one is quoted. */
  highlights: {
    type: Array as PropType<string[] | null>,
    default: null,
  },
  /** The file the answer was read from. */
  fileName: {
    type: String,
    required: true,
  },
  /** Source page; null when the answer has no page (searched, not found). */
  page: {
    type: Number as PropType<number | null>,
    default: null,
  },
});

const emit = defineEmits<{ openPage: [page: number] }>();

const quote = computed(() => props.highlights?.find(text => text.trim().length > 0) ?? null);

const divider = gaiaNeutral.borderSoft;
</script>

<template>
  <!-- The "Agent reasoning" hover card from the Contract View mock: why the
       value reads as it does, the passage it came from, and the way to it.
       Sits in a hover GaiaPopover; the surface is the root so its tokens travel
       with the teleport (see GaiaMenuSurface). -->
  <gaia-menu-surface width="380px">
    <n-flex vertical :size="8" :style="{ padding: '6px 8px' }">
      <gaia-text variant="label" size="nano" tone="subtle">Agent reasoning</gaia-text>
      <gaia-text v-if="props.reason" size="row" tone="text" :style="{ lineHeight: 1.5 }">
        {{ props.reason }}
      </gaia-text>
      <gaia-text v-else size="row" tone="subtle">No reasoning recorded.</gaia-text>
      <gaia-quote v-if="quote">{{ quote }}</gaia-quote>
      <n-flex align="center" justify="space-between" :size="10" :wrap="false" class="source">
        <n-flex vertical :size="2" :style="{ minWidth: 0 }">
          <gaia-text variant="label" size="nano" tone="faint">
            {{ props.page !== null ? 'Source file' : 'Searched file' }}
          </gaia-text>
          <gaia-text size="meta" tone="accent" truncate>{{ props.fileName }}</gaia-text>
        </n-flex>
        <gaia-button
          v-if="props.page !== null"
          size="tiny"
          :title="`Open the document at page ${props.page}`"
          @click="emit('openPage', props.page)"
        >
          Page {{ props.page }}
        </gaia-button>
      </n-flex>
    </n-flex>
  </gaia-menu-surface>
</template>

<style scoped>
.source {
  padding-top: 8px;
  border-top: 1px solid v-bind(divider);
}
</style>
